import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Icons } from '@ohif/ui-next';
import './FormSelectVirtualized.css';

interface FormSelectVirtualizedProps {
  label: string;
  required?: boolean;
  options: { value: string; label: string }[];
  placeholder?: string;
  error?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  name?: string;
  /**
   * Callback when search query changes (for async options)
   */
  onSearchChange?: (query: string) => void;
  /**
   * Whether options are being loaded (for async options)
   */
  isLoading?: boolean;
}

const OPTION_HEIGHT = 40; // Height of each option
const VISIBLE_OPTIONS = 4; // Show 4 options at once
const DROPDOWN_HEIGHT = OPTION_HEIGHT * VISIBLE_OPTIONS; // Total dropdown height
/** Extra pixels past the list bottom before we scroll (subpixel / border tolerance). */
const LIST_SCROLL_EDGE_SLACK_PX = 6;

/**
 * Same as VirtualizedDropdown: `offsetTop` for rows is often relative to the positioned dropdown
 * shell (includes search), not the scroll container — use viewport clipping against the list box.
 */
function adjustScrollContainerToKeepChildVisible(
  container: HTMLDivElement,
  child: HTMLElement,
  bottomSlackPx: number
): void {
  const c = container.getBoundingClientRect();
  const o = child.getBoundingClientRect();

  if (o.top < c.top) {
    container.scrollTop += o.top - c.top;
    return;
  }
  const excessBelow = o.bottom - c.bottom - bottomSlackPx;
  if (excessBelow > 0) {
    container.scrollTop += excessBelow;
  }
}

const FormSelectVirtualized: React.FC<FormSelectVirtualizedProps> = ({
  label,
  required = false,
  options = [],
  placeholder = 'Select',
  error,
  value = '',
  onChange,
  disabled = false,
  name,
  onSearchChange,
  isLoading = false,
}) => {
  const safeOptions = useMemo(() => (Array.isArray(options) ? options : []), [options]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  /** Only scroll the options list when highlight moves via ArrowUp/ArrowDown, not pointer hover. */
  const shouldSyncScrollToHighlightRef = useRef(false);

  // Filter options based on search query (only if not using async options)
  const filteredOptions = useMemo(() => {
    // If using async options (onSearchChange provided), don't filter client-side
    if (onSearchChange) {
      return safeOptions;
    }
    if (!searchQuery.trim()) {
      return safeOptions;
    }
    const query = searchQuery.toLowerCase();
    return safeOptions.filter(option => {
      const optionLabel = String(option?.label ?? '').toLowerCase();
      const optionValue = String(option?.value ?? '').toLowerCase();
      return optionLabel.includes(query) || optionValue.includes(query);
    });
  }, [safeOptions, searchQuery, onSearchChange]);

  const selectedOption = safeOptions.find(opt => opt.value === value);
  const displayValue = selectedOption ? String(selectedOption.label ?? '') : '';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        shouldSyncScrollToHighlightRef.current = false;
        setHighlightedIndex(0);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search without scrolling the page/modal (avoids jarring autoscroll when multiple
      // dropdowns exist or the control is inside a scrollable dialog).
      setTimeout(() => {
        searchInputRef.current?.focus({ preventScroll: true });
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    shouldSyncScrollToHighlightRef.current = false;
    setHighlightedIndex(0);
  }, [filteredOptions.length, searchQuery]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredOptions.length - 1) {
            shouldSyncScrollToHighlightRef.current = true;
            return prev + 1;
          }
          shouldSyncScrollToHighlightRef.current = false;
          return prev;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            shouldSyncScrollToHighlightRef.current = true;
            return prev - 1;
          }
          shouldSyncScrollToHighlightRef.current = false;
          return prev;
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex].value);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery('');
        shouldSyncScrollToHighlightRef.current = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filteredOptions, highlightedIndex]);

  // Scroll list to keep keyboard-highlighted row in view (pointer hover does not move the list).
  useEffect(() => {
    if (!isOpen || !optionsContainerRef.current) {
      return;
    }
    if (!shouldSyncScrollToHighlightRef.current) {
      return;
    }
    shouldSyncScrollToHighlightRef.current = false;

    const optionElement = optionsContainerRef.current.children[highlightedIndex] as HTMLElement;
    if (optionElement) {
      const container = optionsContainerRef.current;
      if (container.clientHeight <= 0) {
        return;
      }
      adjustScrollContainerToKeepChildVisible(container, optionElement, LIST_SCROLL_EDGE_SLACK_PX);
    }
  }, [highlightedIndex, isOpen]);

  const handleSelect = (selectedValue: string) => {
    if (onChange && name) {
      // Create a synthetic event
      const syntheticEvent = {
        target: {
          name,
          value: selectedValue,
        },
        currentTarget: {
          name,
          value: selectedValue,
        },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange(syntheticEvent);
    }
    setIsOpen(false);
    setSearchQuery('');
    shouldSyncScrollToHighlightRef.current = false;
    setHighlightedIndex(0);
    // Clear search in parent hook when option is selected (for async options)
    if (onSearchChange) {
      onSearchChange('');
    }
  };

  const handleToggle = () => {
    if (disabled) {
      return;
    }
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Opening dropdown - clear search
      setSearchQuery('');
      shouldSyncScrollToHighlightRef.current = false;
      setHighlightedIndex(0);
      if (onSearchChange) {
        onSearchChange('');
      }
    } else {
      // Closing dropdown - clear search
      setSearchQuery('');
      shouldSyncScrollToHighlightRef.current = false;
      setHighlightedIndex(0);
      if (onSearchChange) {
        onSearchChange('');
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    setHighlightedIndex(0);
    // Call async search callback if provided - this updates the search query in the hook
    if (onSearchChange) {
      onSearchChange(newQuery);
    }
  };

  return (
    <div
      ref={containerRef}
      className="form-select-virtualized-container"
    >
      {/* Label */}
      <div className="form-select-virtualized-label-wrapper">
        <label className="form-select-virtualized-label">{label}</label>
        {required && <span className="form-select-virtualized-required">*</span>}
      </div>

      {/* Select Field */}
      <div className="form-select-virtualized-select-wrapper">
        <div
          onClick={handleToggle}
          className={`form-select-virtualized-select-field ${
            disabled ? 'form-select-virtualized-select-field-disabled' : ''
          } ${isOpen ? 'form-select-virtualized-select-field-open' : ''} ${
            displayValue ? 'form-select-virtualized-select-field-has-value' : ''
          }`}
        >
          {displayValue || placeholder}
        </div>
        <div
          className={`form-select-virtualized-arrow-icon ${
            isOpen ? 'form-select-virtualized-arrow-icon-open' : ''
          }`}
        >
          <Icons.DownArrowIcon
            width="20"
            height="20"
          />
        </div>

        {/* Dropdown Menu */}
        {isOpen && !disabled && (
          <div
            ref={dropdownRef}
            className="form-select-virtualized-dropdown"
          >
            {/* Search Input */}
            <div className="form-select-virtualized-search-container">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search..."
                className="form-select-virtualized-search-input"
                onKeyDown={e => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
              />
            </div>

            {/* Options List - Virtualized (shows 4 at a time) */}
            <div
              ref={optionsContainerRef}
              className="form-select-virtualized-options-container"
              style={{ maxHeight: `${DROPDOWN_HEIGHT}px` }}
            >
              {isLoading ? (
                <div className="form-select-virtualized-no-options">Loading...</div>
              ) : filteredOptions.length === 0 ? (
                <div className="form-select-virtualized-no-options">No options found</div>
              ) : (
                filteredOptions.map((option, index) => {
                  const isSelected = option.value === value;
                  const isHighlighted = index === highlightedIndex;

                  return (
                    <div
                      key={option.value}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => handleSelect(option.value)}
                      onMouseEnter={() => {
                        shouldSyncScrollToHighlightRef.current = false;
                        setHighlightedIndex(index);
                      }}
                      className={`form-select-virtualized-option ${
                        isSelected && isHighlighted
                          ? 'form-select-virtualized-option-selected-highlighted'
                          : isSelected
                            ? 'form-select-virtualized-option-selected'
                            : isHighlighted
                              ? 'form-select-virtualized-option-highlighted'
                              : ''
                      }`}
                    >
                      {option.label}
                      {isSelected && (
                        <div className="form-select-virtualized-checkmark">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13 4L6 11L3 8"
                              stroke="var(--accent-primary)"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && <span className="form-select-virtualized-error">{error}</span>}
    </div>
  );
};

export default FormSelectVirtualized;
